import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  InputNumber,
  Button,
  Spin,
  App,
  Space,
  Form,
} from 'antd';
import {
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  listCompetitionRegions,
  listAllStates,
  listCitiesByState,
  listFieldTypes,
  listJerseySizes,
} from '../../services/catalogs';
import { getClientsCountReport } from '../../services/reports';
import { exportToExcel } from '../../utils/excel';
import debounce from 'lodash/debounce';

const MarketInventoryReport = () => {
  const { message } = App.useApp();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState([]);
  const [totalGeneral, setTotalGeneral] = useState(0);

  const [form] = Form.useForm();

  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [fieldTypes, setFieldTypes] = useState([]);
  const [jerseySizes, setJerseySizes] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const stateId = Form.useWatch('stateId', form);

  useEffect(() => {
    Promise.all([
      listCompetitionRegions(),
      listAllStates(),
      listFieldTypes(),
      listJerseySizes(),
    ]).then(([z, s, ft, js]) => {
      setZones(z || []);
      setStates(s || []);
      setFieldTypes(ft || []);
      setJerseySizes(js || []);
    });
  }, []);

  useEffect(() => {
    if (stateId) {
      listCitiesByState(stateId).then(setCities);
    } else {
      setCities([]);
      form.setFieldValue('cityId', undefined);
    }
  }, [stateId, form]);

  const fetchCities = useMemo(
    () =>
      debounce(async (value) => {
        if (!value || !stateId) return;
        setLoadingCities(true);
        try {
          const result = await listCitiesByState(stateId, value);
          setCities(result || []);
        } finally {
          setLoadingCities(false);
        }
      }, 500),
    [stateId]
  );

  const buildParams = () => {
    const values = form.getFieldsValue();
    const params = {};
    if (values.zoneId != null) params.zoneId = values.zoneId;
    if (values.stateId != null) params.stateId = values.stateId;
    if (values.cityId != null) params.cityId = values.cityId;
    if (values.fieldTypeId != null) params.fieldTypeId = values.fieldTypeId;
    if (values.jerseySizeId != null) params.jerseySizeId = values.jerseySizeId;
    if (values.shoeSize != null && values.shoeSize !== '') params.shoeSize = String(values.shoeSize);
    return params;
  };

  const hasAtLeastOneFilter = () => {
    const params = buildParams();
    return Object.keys(params).length >= 1;
  };

  const handleSearch = async () => {
    if (!hasAtLeastOneFilter()) {
      message.warning('Debe seleccionar al menos un filtro para consultar.');
      return;
    }
    setLoading(true);
    try {
      const params = buildParams();
      const res = await getClientsCountReport(params);
      if (res?.ok && res?.data) {
        setData(res.data.items || []);
        setTotalGeneral(res.data.totalGeneral ?? 0);
      } else {
        setData([]);
        setTotalGeneral(0);
      }
    } catch (err) {
      message.error(err?.__normalized?.message || 'Error al obtener el reporte.');
      setData([]);
      setTotalGeneral(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setData([]);
    setTotalGeneral(0);
    setCities([]);
  };

  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      message.warning('No hay datos para exportar. Ejecute primero la búsqueda.');
      return;
    }
    setExporting(true);
    try {
      const excelData = data.map((item) => ({
        Zona: item.zona ?? '',
        Estado: item.estado ?? '',
        Ciudad: item.ciudad ?? '',
        'Tipo de cancha': item.tipoCancha ?? '',
        'Talla ropa': item.tallaRopa ?? '',
        'Talla zapatos': item.tallaZapatos ?? '',
        '#': item.cantidad ?? 0,
        '% vs Total': item.porcentajeVsTotal ?? 0,
      }));
      const columnWidths = [
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 16 },
        { wch: 12 },
        { wch: 14 },
        { wch: 8 },
        { wch: 12 },
      ];
      exportToExcel(
        excelData,
        columnWidths,
        'Inventario de mercado',
        'inventario_mercado'
      );
      message.success('Archivo Excel exportado correctamente.');
    } catch (err) {
      message.error('Error al exportar a Excel.');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'Zona',
      dataIndex: 'zona',
      key: 'zona',
      sorter: (a, b) => (a.zona || '').localeCompare(b.zona || ''),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
    },
    {
      title: 'Ciudad',
      dataIndex: 'ciudad',
      key: 'ciudad',
      sorter: (a, b) => (a.ciudad || '').localeCompare(b.ciudad || ''),
    },
    {
      title: 'Tipo de cancha',
      dataIndex: 'tipoCancha',
      key: 'tipoCancha',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.tipoCancha || '').localeCompare(b.tipoCancha || ''),
    },
    {
      title: 'Talla ropa',
      dataIndex: 'tallaRopa',
      key: 'tallaRopa',
      sorter: (a, b) => (a.tallaRopa || '').localeCompare(b.tallaRopa || ''),
    },
    {
      title: 'Talla zapatos',
      dataIndex: 'tallaZapatos',
      key: 'tallaZapatos',
      sorter: (a, b) =>
        (a.tallaZapatos || '').localeCompare(b.tallaZapatos || ''),
    },
    {
      title: '#',
      dataIndex: 'cantidad',
      key: 'cantidad',
      sorter: (a, b) => (a.cantidad ?? 0) - (b.cantidad ?? 0),
      defaultSortOrder: 'descend',
    },
    {
      title: '% vs Total',
      dataIndex: 'porcentajeVsTotal',
      key: 'porcentajeVsTotal',
      render: (v) => (v != null ? `${v}%` : '-'),
      sorter: (a, b) =>
        (a.porcentajeVsTotal ?? 0) - (b.porcentajeVsTotal ?? 0),
    },
  ];

  const dataSource = data.map((item, idx) => ({
    ...item,
    key: idx,
  }));

  return (
    <Card
      title="Reportes - Inventario de Mercado"
      style={{ margin: 20 }}
      extra={
        <Space>
          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={handleSearch}
            loading={loading}
          >
            Buscar
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleClear}>
            Limpiar
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            loading={exporting}
            disabled={!data.length}
          >
            Exportar Excel
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="zoneId" label="Zona">
              <Select
                allowClear
                placeholder="Zona"
                options={zones.map((z) => ({
                  value: z.id,
                  label: z.description,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="stateId" label="Estado">
              <Select
                allowClear
                placeholder="Estado"
                options={states.map((s) => ({
                  value: s.id,
                  label: s.description,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="cityId" label="Ciudad">
              <Select
                allowClear
                showSearch
                filterOption={false}
                onSearch={fetchCities}
                loading={loadingCities}
                placeholder="Ciudad"
                disabled={!stateId}
                options={cities.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="fieldTypeId" label="Tipo de cancha">
              <Select
                allowClear
                placeholder="Tipo cancha"
                options={fieldTypes.map((f) => ({
                  value: f.id,
                  label: f.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="jerseySizeId" label="Talla de ropa">
              <Select
                allowClear
                placeholder="Talla ropa"
                options={jerseySizes.map((j) => ({
                  value: j.id,
                  label: j.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Form.Item name="shoeSize" label="Talla de zapato">
              <InputNumber
                min={20}
                max={50}
                placeholder="Talla zapato"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} registros`,
          }}
          scroll={{ x: 'max-content' }}
          summary={() =>
            totalGeneral > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>
                  <strong>Total general</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{totalGeneral}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>100%</Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      </Spin>
    </Card>
  );
};

export default MarketInventoryReport;
